import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageLoader, PageHeader } from "@/components/shared";
import { Phone, Copy, ExternalLink, Search, BadgeCheck, Landmark, Info, Wrench } from "lucide-react";

const STAGES = ["Everything", "Buying", "Selling", "New homeowner", "Moving to Oahu / military"];

function copyPhone(phone) {
  navigator.clipboard?.writeText(phone).then(
    () => toast.success("Phone number copied"),
    () => toast.error("Couldn't copy")
  );
}

function VendorCard({ v, i }) {
  const official = v.type === "official_resource";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}
      className="hg-card p-5" data-testid={`vendor-card-${v.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h4 className="font-semibold leading-tight">{v.name}</h4>
        {official ? (
          <Badge className="border-0 bg-forest/15 text-forest shrink-0 gap-1"><Landmark className="h-3 w-3" /> Official resource</Badge>
        ) : (
          <Badge className="border-0 bg-primary/15 text-primary shrink-0 gap-1"><BadgeCheck className="h-3 w-3" /> Marlene's pick</Badge>
        )}
      </div>
      {v.description && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{v.description}</p>}
      {v.service_area && <p className="text-xs text-muted-foreground mt-2"><span className="font-medium text-foreground/80">Serves:</span> {v.service_area}</p>}
      {v.license_shown_on_site && <p className="text-xs text-muted-foreground mt-1">License {v.license_shown_on_site}</p>}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {v.phone && (
          <div className="inline-flex items-center gap-1.5 rounded-full bg-secondary pl-3 pr-1.5 py-1 text-sm" data-testid={`vendor-phone-${v.id}`}>
            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
            <a href={`tel:${v.phone.replace(/[^+\d]/g, "")}`} className="font-medium hover:underline">{v.phone}</a>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyPhone(v.phone)} data-testid={`vendor-copy-${v.id}`} aria-label="Copy phone">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        {v.website && (
          <a href={v.website} target="_blank" rel="noreferrer" data-testid={`vendor-website-${v.id}`}
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
            Visit website <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function VendorGuide() {
  const [stage, setStage] = useState("Everything");
  const [q, setQ] = useState("");
  const vQ = useQuery({ queryKey: ["buyer-vendors"], queryFn: async () => (await api.get("/buyer/vendors")).data });

  const { vendors = [], tips = {} } = vQ.data || {};

  const grouped = useMemo(() => {
    const query = q.trim().toLowerCase();
    const filtered = vendors.filter((v) => {
      if (stage !== "Everything" && !(v.stages || []).includes(stage)) return false;
      if (!query) return true;
      return [v.name, v.description, v.category, v.service_area].filter(Boolean).join(" ").toLowerCase().includes(query);
    });
    const order = [];
    const map = {};
    for (const v of filtered) {
      if (!map[v.category]) { map[v.category] = []; order.push(v.category); }
      map[v.category].push(v);
    }
    return order.map((cat) => ({ category: cat, items: map[cat] }));
  }, [vendors, stage, q]);

  if (vQ.isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader title="Vendor guide" subtitle="Local Oahu vendors and official resources to help you through buying, selling, settling in, and moving to the island. Review and choose whoever's right for you." />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="vendor-stage-chips">
        {STAGES.map((s) => (
          <button key={s} onClick={() => setStage(s)} data-testid={`vendor-chip-${s.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
            className={`text-sm px-3.5 py-1.5 rounded-full font-medium transition-colors ${stage === s ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="relative mb-8 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors (e.g. plumber, Kailua, movers)…" className="pl-9 h-11 rounded-xl" data-testid="vendor-search" />
      </div>

      {grouped.length === 0 ? (
        <Card className="p-12 rounded-2xl text-center" data-testid="vendor-empty">
          <Wrench className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-heading text-lg font-semibold">No matches</p>
          <p className="text-muted-foreground text-sm mt-1">Try a different filter or search term.</p>
        </Card>
      ) : (
        <div className="space-y-10">
          {grouped.map(({ category, items }) => (
            <section key={category} data-testid={`vendor-category-${category.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
              <h3 className="font-heading font-semibold text-lg">{category}</h3>
              {tips[category] && (
                <p className="text-sm text-muted-foreground mt-1 flex items-start gap-1.5 max-w-3xl">
                  <Info className="h-4 w-4 text-forest shrink-0 mt-0.5" /> {tips[category]}
                </p>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
                {items.map((v, i) => <VendorCard key={v.id} v={v} i={i} />)}
              </div>
            </section>
          ))}
        </div>
      )}

      <Card className="p-5 rounded-2xl mt-10 bg-secondary/50" data-testid="vendor-footer">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Provided for your convenience. You may choose any provider. Get your own quotes and confirm licensing and insurance before hiring. Marlene Alesna and eXp Realty receive no compensation from any vendor listed. Check any Hawaii license at mypvl.dcca.hawaii.gov.
        </p>
      </Card>
    </div>
  );
}
